import { throttle } from '../utils/utilities';

const CLASSES = {
    scrolling_down: 'is-scrolling-down',
    sticky: 'is-sticky',
};

export const Header = (header) => {
    if (!header) return () => {};

    const headerScrollingClass = toggleScrollingClass(header);
    const headerStickyClass = toggleStickyClass(header);

    // Return combined cleanup function
    return () => {
        headerScrollingClass.cleanup();
        headerStickyClass.cleanup();
    };
};

// Minimum travel before the header is allowed to flip direction. Lenis runs with
// `syncTouch: true`, which drives scrollY itself on touch devices and emits
// repeated or sub-pixel-reversed values mid-gesture; without a threshold a single
// such frame un-hides the header and the next frame hides it again.
const SCROLL_DELTA = 8;

const toggleScrollingClass = (header) => {
    let lastScrollTop = 0;
    let siteHeaderHeight = header.offsetHeight;
    let ticking = false;

    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            if (entry.borderBoxSize?.[0]) {
                siteHeaderHeight = entry.borderBoxSize[0].blockSize;
            } else {
                siteHeaderHeight = entry.target.offsetHeight;
            }
        }
    });

    resizeObserver.observe(header);

    const handleScroll = () => {
        if (ticking) return;

        ticking = true;
        requestAnimationFrame(() => {
            // Clamp: iOS rubber-banding reports a negative scrollY past the top.
            const scrollTop = Math.max(window.scrollY || document.documentElement.scrollTop, 0);
            const delta = scrollTop - lastScrollTop;

            if (scrollTop < siteHeaderHeight) {
                // Still within the header's own height - always reveal.
                header.classList.remove(CLASSES.scrolling_down);
            } else if (Math.abs(delta) >= SCROLL_DELTA) {
                header.classList.toggle(CLASSES.scrolling_down, delta > 0);
            } else {
                // Below the threshold: leave the current state alone and keep
                // lastScrollTop where it is so slow scrolls still accumulate
                // toward SCROLL_DELTA instead of being discarded frame by frame.
                ticking = false;

                return;
            }

            lastScrollTop = scrollTop;
            ticking = false;
        });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return {
        cleanup: () => {
            window.removeEventListener('scroll', handleScroll);
            resizeObserver.disconnect();
        },
    };
};

const toggleStickyClass = (header) => {
    const sticky = 0;

    const stickyHeader = () => {
        if (window.scrollY > sticky) {
            header.classList.add(CLASSES.sticky);
        } else {
            header.classList.remove(CLASSES.sticky);
        }
    };

    stickyHeader();

    const scrollCallback = throttle(stickyHeader, 100);
    window.addEventListener('scroll', scrollCallback);

    return {
        cleanup: () => {
            window.removeEventListener('scroll', scrollCallback);
        },
    };
};
